import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Shield, Zap, HardHat } from 'lucide-react';

export default function Hero() {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-ferry-darker via-ferry-dark to-ferry-gray" />
        <div className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        {/* Gradient orbs */}
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-ferry-crimson/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-ferry-crimson/5 rounded-full blur-[128px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-32 pb-20 w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ferry-crimson/10 border border-ferry-crimson/20 mb-8">
                <Shield size={14} className="text-ferry-crimson" />
                <span className="text-sm font-medium text-ferry-cream">Veilig & Betrouwbaar</span>
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
            >
              Veilig werken aan de{' '}
              <span className="gradient-text">energie</span>{' '}
              van morgen
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-lg md:text-xl text-ferry-gray-text leading-relaxed mb-10 max-w-lg"
            >
              Ferry Infra is uw specialist in infrastructuuroplossingen voor de energiesector. 
              Wij leveren vakmanschap, veiligheid en kwaliteit op elk project.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={() => scrollTo('#contact')}
                className="px-8 py-4 bg-ferry-crimson hover:bg-ferry-crimson-light text-white font-semibold rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-ferry-crimson/25 hover:-translate-y-0.5"
              >
                Neem Contact Op
              </button>
              <button
                onClick={() => scrollTo('#services')}
                className="px-8 py-4 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-xl border border-white/10 hover:border-white/20 transition-all duration-300"
              >
                Onze Diensten
              </button>
            </motion.div>
          </div>

          {/* Right Content - Feature Cards */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hidden lg:grid grid-cols-2 gap-4"
          >
            {[
              { icon: Shield, title: 'Veiligheid', desc: 'VCA** gecertificeerd en altijd conform de laatste veiligheidsnormen' },
              { icon: Zap, title: 'Energie', desc: 'Specialist in gas, water en elektra infrastructuur' },
              { icon: HardHat, title: 'Vakmanschap', desc: 'Ervaren vakmensen met jarenlange expertise in de sector' },
              { icon: ArrowDown, title: 'Kwaliteit', desc: 'ISO gecertificeerd met focus op duurzame resultaten', iconRotate: true },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
                className={`glass-card p-6 hover:bg-white/10 transition-all duration-300 group ${
                  i === 1 ? 'translate-y-8' : i === 3 ? 'translate-y-8' : ''
                }`}
              >
                <div className="w-12 h-12 rounded-xl bg-ferry-crimson/10 flex items-center justify-center mb-4 group-hover:bg-ferry-crimson/20 transition-colors duration-300">
                  <item.icon size={22} className="text-ferry-crimson" />
                </div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-ferry-gray-text leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center gap-2 cursor-pointer"
            onClick={() => scrollTo('#about')}
          >
            <span className="text-xs text-ferry-gray-text uppercase tracking-widest">Scroll</span>
            <ArrowDown size={16} className="text-ferry-crimson" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
